import { EntityType } from '@frontend/common';
import { ErrorHandler } from '@frontend/error-handler';
import { Logger } from '@frontend/logger';
import { PlaylistItemClient } from '@frontend/playlist/api';
import { PlaylistItem } from '@frontend/playlist/types';
import { useBlobRepository, useDocumentRepository } from '@frontend/repository';
import { SettingKey, SettingValue } from '@frontend/setting/types';
import { useEffect, useRef, useState } from 'react';

import { useAppSelector } from '../../redux/store';

interface ViewProps {
    display?: string | null;
    url: string | null;
    playlistId?: string | null;
    allowUserIdentification: boolean;
    isLoggedIn: boolean;
    isInterrupted: boolean;
    interruptCarousel: () => void;
}

const DEFAULT_CAROUSEL_INTERRUPT_TIMEOUT_SECONDS = 30;

const toBooleanSetting = (value: SettingValue | undefined): boolean => value === true || value === 'true' || value === 1 || value === '1';

const parsePositiveNumberSetting = (value: SettingValue | undefined): number | undefined => {
    if (typeof value === 'number' && !isNaN(value) && value > 0) return value;
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        if (!isNaN(parsed) && parsed > 0) return parsed;
    }
    return undefined;
};

const playlistItemsCache = new Map<string, PlaylistItem[]>();

const useCarousel = (): ViewProps => {
    const navigationState = useAppSelector((store) => store.navigation);
    const blobRepository = useBlobRepository();
    const documentRepository = useDocumentRepository();
    const accountId = navigationState.iot?.account_id;
    const spotSettings = navigationState.spot?.settings;
    const playlistId = spotSettings?.[SettingKey.DEFAULT_PLAYLIST_ID] as string | undefined;
    const cachedItems = playlistId ? (playlistItemsCache.get(playlistId) ?? null) : null;
    const [playlistItems, setPlaylistItems] = useState<PlaylistItem[] | null>(cachedItems);
    const [current, setCurrent] = useState<PlaylistItem | null>(cachedItems?.[0] ?? null);
    const [url, setUrl] = useState<string | null>(null);
    const [isInterrupted, setIsInterrupted] = useState<boolean>(false);
    const allowCarouselInterrupt = toBooleanSetting(spotSettings?.[SettingKey.ALLOW_CAROUSEL_INTERRUPT]);
    const carouselInterruptTimeoutSeconds =
        parsePositiveNumberSetting(spotSettings?.[SettingKey.INACTIVITY_TIMEOUT_SECONDS]) ?? DEFAULT_CAROUSEL_INTERRUPT_TIMEOUT_SECONDS;
    const allowUserIdentification = spotSettings ? !!spotSettings[SettingKey.ALLOW_USER_IDENTIFICATION] : false;
    const isLoggedIn = navigationState.user !== null;
    const preWarmingRef = useRef(false);

    const preWarmBlobs = (items: PlaylistItem[]) => {
        if (!accountId || preWarmingRef.current) return;
        preWarmingRef.current = true;
        Promise.all(items.map((item) => blobRepository.getBlob(accountId, EntityType.PLAYLIST_ITEM, item.id).catch(() => null))).finally(() => {
            preWarmingRef.current = false;
        });
    };

    useEffect(() => {
        let isDisposed = false;

        if (!playlistId) {
            setPlaylistItems(null);
            setCurrent(null);
            setUrl(null);
            return;
        }

        const cached = playlistItemsCache.get(playlistId);
        if (cached) {
            setPlaylistItems(cached);
            setCurrent(cached[0] ?? null);
            preWarmBlobs(cached);
        }

        PlaylistItemClient.fetchPlaylistItems({ playlist_id: playlistId })
            .then((result) => {
                if (isDisposed) return;
                const ordered = result.results.filter((i) => i.visible).sort((a, b) => a.sort_order - b.sort_order);
                playlistItemsCache.set(playlistId, ordered);
                setPlaylistItems(ordered);
                if (!cached) setCurrent(ordered[0] ?? null);
                preWarmBlobs(ordered);
                documentRepository
                    .preLoadFiles(
                        EntityType.PLAYLIST_ITEM,
                        ordered.map((i) => i.id)
                    )
                    .catch(ErrorHandler.handleError);
            })
            .catch((e) => {
                if (!cached) ErrorHandler.handleError(e);
                else Logger.warn('Background carousel refresh failed, using cached data');
            });

        return () => {
            isDisposed = true;
        };
    }, [playlistId]);

    useEffect(() => {
        let isDisposed = false;
        let timeout: ReturnType<typeof setTimeout> | undefined;

        const loadUrl = async (playlistItemId: string) => {
            const stale = accountId ? blobRepository.getBlobStale(accountId, EntityType.PLAYLIST_ITEM, playlistItemId) : null;
            if (stale) {
                setUrl(stale.url);
                return;
            }

            setUrl(null);

            try {
                if (accountId) {
                    const blobInfo = await blobRepository.getBlob(accountId, EntityType.PLAYLIST_ITEM, playlistItemId);
                    if (isDisposed) return;
                    if (blobInfo) {
                        setUrl(blobInfo.url);
                        return;
                    }
                }
            } catch (error) {
                ErrorHandler.handleError({ error: error instanceof Error ? error : new Error(String(error)) });
            }

            try {
                const service = await documentRepository.getFiles(EntityType.PLAYLIST_ITEM, playlistItemId);
                if (isDisposed) return;
                const document = service.getDocuments()[0];
                if (document) {
                    const documentUrl = await service.getUrl(document.id);
                    if (!isDisposed) setUrl(documentUrl);
                }
            } catch (error) {
                ErrorHandler.handleError({ error: error instanceof Error ? error : new Error(String(error)) });
            }
        };

        if (playlistItems && current) {
            loadUrl(current.id);
            timeout = setTimeout(() => {
                const currentIndex = playlistItems.indexOf(current);
                if (currentIndex !== -1) {
                    if (currentIndex + 1 < playlistItems.length) {
                        setCurrent(playlistItems[currentIndex + 1]);
                    } else {
                        setCurrent(playlistItems[0]);
                    }
                }
            }, current.duration * 1000);
        }

        return () => {
            isDisposed = true;
            if (timeout) clearTimeout(timeout);
        };
    }, [accountId, current, playlistItems]);

    useEffect(() => {
        if (!isInterrupted) return;
        if (isLoggedIn || !allowCarouselInterrupt) {
            setIsInterrupted(false);
            return;
        }

        const timeout = setTimeout(() => {
            setIsInterrupted(false);
        }, carouselInterruptTimeoutSeconds * 1000);

        return () => clearTimeout(timeout);
    }, [allowCarouselInterrupt, carouselInterruptTimeoutSeconds, isInterrupted, isLoggedIn]);

    const interruptCarousel = () => {
        if (!allowCarouselInterrupt || isLoggedIn) return;
        setIsInterrupted(true);
    };

    return {
        display: navigationState.iot?.display,
        url,
        playlistId,
        allowUserIdentification,
        isLoggedIn,
        isInterrupted,
        interruptCarousel
    };
};

export default useCarousel;
